const STORAGE_KEY_PREFIX = "recsys.pinnedProfiles.";

export function pinnedProfilesStorageKey(username: string): string {
  return `${STORAGE_KEY_PREFIX}${username}`;
}

export function readPinnedIds(username: string): string[] {
  if (!username) return [];
  try {
    const raw = localStorage.getItem(pinnedProfilesStorageKey(username));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === "string" && id.length > 0);
  } catch {
    return [];
  }
}

export function writePinnedIds(username: string, ids: string[]): void {
  if (!username) return;
  try {
    localStorage.setItem(pinnedProfilesStorageKey(username), JSON.stringify(ids));
  } catch {
    // localStorage unavailable — pins won't persist
  }
}

/** Newest-first: pinning moves id to index 0; already pinned → unpin. */
export function togglePinnedId(ids: string[], id: string): string[] {
  if (ids.includes(id)) {
    return ids.filter((pinnedId) => pinnedId !== id);
  }
  return [id, ...ids.filter((pinnedId) => pinnedId !== id)];
}

export function removePinnedId(ids: string[], id: string): string[] {
  return ids.filter((pinnedId) => pinnedId !== id);
}

/** Stable sort: pinned first in pin-order; unpinned keep relative order. Orphan pins ignored. */
export function sortWithPins<T extends { id: string }>(
  items: T[],
  pinnedIds: string[],
): T[] {
  if (pinnedIds.length === 0 || items.length === 0) return items;

  const byId = new Map(items.map((item) => [item.id, item]));
  const pinned: T[] = [];
  const seen = new Set<string>();

  for (const id of pinnedIds) {
    const item = byId.get(id);
    if (!item || seen.has(id)) continue;
    pinned.push(item);
    seen.add(id);
  }

  const unpinned = items.filter((item) => !seen.has(item.id));
  return [...pinned, ...unpinned];
}
